
import { Component, Vue } from 'vue-property-decorator';
import QvInput from '@/components/FormComponents/QvInput.vue';
import QvSelect from '@/components/FormComponents/QvSelect.vue';
import { ValidationObserver } from 'vee-validate';
import {
  FarmStore,
  CommonStore,
  ConfigurationStore,
  BlockStore,
  FloweringStore,
  ChartStore
} from '@/store/modules';
import LocationInputMap from '@/components/Forms/Components/LocationInputMap.vue';
import helpers from '@/utils/helpers';
import {
  DryMatterConfiguration,
  EstimatedDMPercentIncrease
} from '@/store/models/configuration';
import { RawChart, Series } from '@/store/models/charts';

import BaseChart from '@/components/Charts/Base.vue';
import Highcharts, { Options, XAxisOptions } from 'highcharts';

@Component({
  components: {
    ValidationObserver,
    QvInput,
    QvSelect,
    LocationInputMap,
    BaseChart
  }
})
export default class FarmForm extends Vue {
  $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
    dmIncreaseChart: InstanceType<typeof BaseChart>;
  };

  dryMatterConfiguration: DryMatterConfiguration = {} as DryMatterConfiguration;

  estimatedDmPercentIncrease: EstimatedDMPercentIncrease = {} as EstimatedDMPercentIncrease;
  reRender = 0;

  cropId: number | null = null;
  currentSeasonId: number | null = null;

  allSelected: boolean | null = false;

  blockSeries: { [key in string | number]: number } = {};
  calculatePercentDmIncrease: any = {};

  plottingPoints() {
    const dataLin: number[][] = [];
    if (this.blockSeries) {
      const timestamps = Object.keys(this.blockSeries);
      const x = this.calculatePercentDmIncrease.x;
      const m = this.calculatePercentDmIncrease.slope;
      const c = this.calculatePercentDmIncrease.intercept;
      let index = 0;
      for (const timestamp of timestamps) {
        dataLin.push([Number(timestamp), m * x[index] + c]);
        index++;
      }
    }
    return dataLin;
  }

  get dmIncreaseChartData() {
    const rawData: RawChart = {
      title: 'Estimated Rate of DM Increased (%)',
      series: { Observation: this.blockSeries },
      extra: {}
    };

    const chartOptions: Options = ChartStore.ScatterChartData(rawData);
    console.log(chartOptions);
    chartOptions.series?.push({
      type: 'spline',
      name: 'Estimated Rate of DM Increased',
      marker: {
        enabled: false
      },
      data: this.plottingPoints(),
      states: {
        hover: {
          lineWidth: 0
        }
      },
      enableMouseTracking: false
    });
    if (
      this.calculatePercentDmIncrease &&
      this.calculatePercentDmIncrease.targetSeries
    ) {
      const data = this.calculatePercentDmIncrease.targetSeries as any[];
      data[1] = {
        x: data[1][0],
        y: data[1][1],
        marker: {
          enabled: true
        }
      };
      chartOptions.series?.push({
        type: 'spline',
        name: 'Target',
        marker: {
          enabled: false
        },
        data: data,
        states: {
          hover: {
            lineWidth: 0
          }
        }
      });
    }
    const xAxis: XAxisOptions = {
      type: 'datetime',
      labels: {
        formatter: function() {
          return Highcharts.dateFormat('%b-%d', this.value as number);
        }
      }
    };

    chartOptions.xAxis = xAxis;

    if (chartOptions.plotOptions) {
      chartOptions.plotOptions.series = {
        dataLabels: {
          enabled: false,
          overflow: 'justify'
        }
      };
      if (chartOptions.plotOptions.series) {
        chartOptions.plotOptions.series.tooltip = {
          xDateFormat: '%Y-%m-%d',
          pointFormatter: function() {
            const date = new Date(this.x).toJSON().split('T')[0];
            return `<strong>${date}:</strong> ${this.y}`;
          }
        };
      }
    }
    chartOptions.tooltip = {
      xDateFormat: '%Y-%m-%d'
    };

    const colors = [
      '#f7a35c',
      '#90ed7d',
      '#f15c80',
      '#7cb5ec',
      '#8085e9',
      '#e4d354',
      '#2b908f',
      '#f45b5b',
      '#434348',
      '#91e8e1'
    ];
    if (chartOptions.series) {
      for (const [index, series] of chartOptions.series.entries()) {
        series.color = colors[index];
      }
    }
    this.updateCharts();
    return chartOptions;
  }

  updateCharts() {
    this.$nextTick(() => {
      if (this.$refs.dmIncreaseChart) {
        this.$refs.dmIncreaseChart.redraw();
      }
    });
  }

  checkAll(value: boolean | null) {
    if (value) {
      this.$set(
        this.estimatedDmPercentIncrease,
        'blocks',
        this.blocks.map(option => option.value)
      );
      this.reRender = new Date().getTime();
      return;
    }
    this.$set(this.estimatedDmPercentIncrease, 'blocks', []);
    this.reRender = new Date().getTime();
  }

  checkVals(value: string[]) {
    this.allSelected =
      value.length > 0 && value.length < this.blocks.length
        ? null
        : value.length === this.blocks.length
        ? true
        : false;
  }

  get currentFarm() {
    return CommonStore.SelectedFarmBlock;
  }

  get crops() {
    const crops = BlockStore.SeasonCrops.map(x => {
      return {
        value: x.id,
        variety: x.variety,
        label: `${x.cropTypeName} - ${x.varietyName}`
      };
    });
    return crops;
  }

  get seasons() {
    this.currentSeasonId = null;
    let seasons = ConfigurationStore.Seasons.map(x => {
      return { value: x.id, label: x.name, ...x };
    });

    if (this.cropId != 0) {
      seasons = seasons.filter(x => x.crop == this.cropId);
    }
    return seasons;
  }

  get allBlocks() {
    return BlockStore.SimpleBlocks.filter(
      x => x.farm == this.currentFarm.farmId
    ).map(x => {
      return { value: x.id, label: x.name, crop: x.crop };
    });
  }

  get blocks() {
    if (this.currentSeasonId != null) {
      return BlockStore.SeasonWiseSimpleBlocks.map(x => {
        return { value: x.id, label: x.name };
      });
    }
    return [];
  }

  async onSubmit() {
    if (this.dryMatterConfiguration.id && this.dryMatterConfiguration.id != 0) {
      await ConfigurationStore.updateDryMatterConfiguration(
        this.dryMatterConfiguration
      );
      this.$q.notify({
        message: 'Harvest load configuration updated successfully',
        type: 'positive'
      });
    }
  }

  onReset() {
    requestAnimationFrame(() => {
      this.$refs.observer.reset();
      this.$router.push({ name: 'Settings' });
    });
  }

  onBlockSelect(blockId: number) {
    const block = this.allBlocks.find(x => x.value == blockId);
    if (block) {
      this.cropId = block.crop;
    }
    this.currentSeasonId = null;
    this.estimatedDmPercentIncrease.dmPercentIncrease = '';
    this.reRender = new Date().getTime();
    this.onSeasonChanged();
  }

  get farms() {
    return FarmStore.Farms;
  }

  async loadFarm() {
    await ConfigurationStore.getDryMatterConfigurationByFarmId(
      this.currentFarm.farmId
    );
    this.dryMatterConfiguration = ConfigurationStore.DryMatterConfiguration;
    this.dryMatterConfiguration = {
      id: this.dryMatterConfiguration.id,
      dmPercentIncrease: this.dryMatterConfiguration.dmPercentIncrease,
      dmTarget: this.dryMatterConfiguration.dmTarget
    };
  }

  async onSeasonChanged() {
    this.loadBlocks();
    if (this.estimatedDmPercentIncrease.observedBlock && this.currentSeasonId) {
      this.calculatePercentDmIncrease = await FloweringStore.calculatePercentDmIncrease(
        {
          blockId: this.estimatedDmPercentIncrease.observedBlock,
          seasonId: this.currentSeasonId
        }
      );
      this.estimatedDmPercentIncrease.dmPercentIncrease = this.calculatePercentDmIncrease.dmPercentIncreased;
      this.estimatedDmPercentIncrease.blocks = this.calculatePercentDmIncrease.blocks;
      this.blockSeries = this.calculatePercentDmIncrease.series;
      this.reRender = new Date().getTime();
    }
  }

  async loadBlocks() {
    await BlockStore.getSeasonWiseSimpleBlocks({
      farm: this.currentFarm.farmId,
      cropSeasonCalendarsSeasons: this.currentSeasonId
    });
  }

  clearEstimatedDmPercentIncreaseBlocks() {
    this.estimatedDmPercentIncrease.blocks = [];
    this.reRender = new Date().getTime();
  }

  async onEstimatedDMPercentageSave() {
    if (this.currentSeasonId) {
      this.estimatedDmPercentIncrease.season = this.currentSeasonId;
    }
    await FloweringStore.updateSeasonsDmPercentIncrease(
      this.estimatedDmPercentIncrease
    );
    this.$q.notify({
      message: 'Updated estimated dm percentage successfully',
      type: 'positive'
    });
  }

  async created() {
    await helpers.fetchingDefaultFarm();
    this.loadFarm();
    BlockStore.getSimpleBlocks({
      farm: this.currentFarm.farmId
    });
  }
}
